import { compact } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  visibleForManager,
  visibleForPeers,
} from '../../pages/PerformanceReview/EmployeePerformanceReview/EmployeeFeedbacks/utils';
import {
  EmployeeFeedbackAnswer,
  FeedbackQuestionType,
  UpdateEmployeeFeedbackAnswerInput,
} from '../../__generated__/graphql';
import { EmployeeFeedbackField } from './EmployeeFeedbackField';
import { FeedbackCard } from './EmployeeFeedbackQuestion';
import { EmployeeFeedbackAccordion } from './EmployeeFeedbackAccordion';
import {
  EmployeeFeedbackAnswerProps,
  FeedbackCycleQuestionsSettings,
  IEmployeeFeedbackAnswer,
  IScorecard,
} from './types';
import { convertToScorecards } from './utils';
import { Col, Row } from 'react-bootstrap';
import { FeedbackAnswer } from '../EmployeeFeedback/FeedbackAnswer';
import { mapToComparedFeedbackAnswer } from '../../hooks/useComparedFeedbackSectionsMapper';
import { FeedbackAnswerSection } from '../EmployeeFeedback/SideBySideAnswers';
import { Maybe } from '../../../../utils/maybe';
import { FeedbackCycleSettingsProvider } from '../../context/feedbackCycle/FeedbackCycleSettingsContext';

type AnswerForMapType = Pick<
  EmployeeFeedbackAnswer,
  | 'Id'
  | 'flair__Employee_Feedback_Question__c'
  | 'flair__Note__c'
  | 'feedbackAnswer'
>;

type Props = {
  answers: ReadonlyArray<EmployeeFeedbackAnswerProps>;
  setIsComplete: (value: boolean) => void;
  onAnswerToSave: (answerToSave: UpdateEmployeeFeedbackAnswerInput) => void;
  readOnly?: boolean;
  seeAllQuestions?: boolean;
  isPeerReviewer: boolean;
  employeeAnswers?: Maybe<EmployeeAnswers>;
  managerAnswers?: Maybe<ManagerAnswers>;
  questionsSettings: FeedbackCycleQuestionsSettings;
};

const isScorecardComlete = (
  value: string | null,
  requiredChoices: ReadonlyArray<string>,
): boolean => {
  if (!requiredChoices || requiredChoices.length === 0) {
    return true;
  }

  const scorecards: IScorecard[] = compact(convertToScorecards(value || ''));

  for (const requiredChoice of requiredChoices) {
    const scorecard = scorecards.find((item) => item.name === requiredChoice);
    if (!scorecard || scorecard.score === undefined) {
      return false;
    }
  }

  return true;
};

const isFieldOptional = (
  optional: boolean,
  type: FeedbackQuestionType,
  requiredChoices: ReadonlyArray<string>,
) =>
  (type === FeedbackQuestionType.Scorecard && requiredChoices.length === 0) ||
  optional;

const isFieldComplete = (
  value: string | null,
  optional: boolean,
  type: FeedbackQuestionType,
  requiredChoices: ReadonlyArray<string>,
): boolean => {
  switch (type) {
    case FeedbackQuestionType.Scorecard: {
      return isScorecardComlete(value, requiredChoices);
    }

    case FeedbackQuestionType.FreeSlider:
      return optional || (value !== undefined && value !== null);
    case FeedbackQuestionType.FreeText:
    case FeedbackQuestionType.Dropdown:
    case FeedbackQuestionType.SingleChoice:
    case FeedbackQuestionType.MultipleChoice:
    case FeedbackQuestionType.Unknown:
    default:
      return optional || !!value;
  }
};

type SectionProps = {
  sectionName: string | null;
  answers: IEmployeeFeedbackAnswer[];
  onUpdateAnswer: (
    answer: IEmployeeFeedbackAnswer,
    value: string,
    notes: string,
  ) => void;
  readOnly: boolean;
  employeeAnswers?: Maybe<EmployeeAnswers>;
  managerAnswers?: Maybe<ManagerAnswers>;
};

type EmployeeAnswers = {
  answers: ReadonlyArray<AnswerForMapType>;
  employeeName: string;
  submittedAt: Maybe<string>;
  showNotSubmittedPlaceholder?: boolean;
};

type ManagerAnswers = {
  answers: ReadonlyArray<AnswerForMapType>;
  managerName: string;
  submittedAt: Maybe<string>;
  showNotSubmittedPlaceholder?: boolean;
};

const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({
  sectionName,
  answers,
  onUpdateAnswer,
  readOnly,
  employeeAnswers,
  managerAnswers,
}) => (
  <EmployeeFeedbackAccordion title={sectionName}>
    {answers.map((answer) => {
      let employeeAnsMapped;
      let employeeName = '';

      if (employeeAnswers) {
        const employeeAns = employeeAnswers.answers.find(
          (ea) =>
            ea.flair__Employee_Feedback_Question__c ===
            answer.questionOriginal.Id,
        );

        employeeAnsMapped = employeeAns
          ? mapToComparedFeedbackAnswer(employeeAns, answer.questionOriginal)
          : null;

        employeeName = employeeAnswers.employeeName;
      }

      let managerAnsMapped;
      let managerName = '';

      if (managerAnswers) {
        const managerAns = managerAnswers.answers.find(
          (ma) =>
            ma.flair__Employee_Feedback_Question__c ===
            answer.questionOriginal.Id,
        );

        managerAnsMapped = managerAns
          ? mapToComparedFeedbackAnswer(managerAns, answer.questionOriginal)
          : null;

        managerName = managerAnswers.managerName;
      }

      // Show a "Not yet submitted" placeholder only when the caller opts in
      // (peer reviewers). Non-peer managers in sequential mode must keep the
      // legacy behavior of rendering nothing until there's a matching answer.
      const showEmployeeReference =
        employeeAnswers &&
        (employeeAnsMapped ||
          (employeeAnswers.showNotSubmittedPlaceholder &&
            !employeeAnswers.submittedAt));
      const showManagerReference =
        managerAnswers &&
        (managerAnsMapped ||
          (managerAnswers.showNotSubmittedPlaceholder &&
            !managerAnswers.submittedAt));
      const hasReferenceAnswers = showEmployeeReference || showManagerReference;

      return (
        <Row className="border-bottom mb-4" key={answer.id}>
          {hasReferenceAnswers && (
            <Col>
              {showEmployeeReference && (
                <FeedbackCard>
                  <FeedbackAnswerSection
                    questionVisibility={
                      answer.questionOriginal
                        .flair__Feedback_Question_Visibility__c
                    }
                    answerVisibility={
                      answer.questionOriginal.flair__Answer_Visibility__c
                    }
                    isAnswerAuthor={false}
                    isEmployeeSection={true}
                    employeeName={employeeName}
                    isSubmitted={!!employeeAnswers.submittedAt}>
                    {employeeAnsMapped && (
                      <FeedbackAnswer
                        answer={employeeAnsMapped}
                        employeeName={employeeName}
                        side="left"
                      />
                    )}
                  </FeedbackAnswerSection>
                </FeedbackCard>
              )}
              {showManagerReference && (
                <FeedbackCard>
                  <FeedbackAnswerSection
                    questionVisibility={
                      answer.questionOriginal
                        .flair__Feedback_Question_Visibility__c
                    }
                    answerVisibility={
                      answer.questionOriginal.flair__Answer_Visibility__c
                    }
                    isAnswerAuthor={false}
                    isEmployeeSection={false}
                    employeeName={managerName}
                    isSubmitted={!!managerAnswers.submittedAt}>
                    {managerAnsMapped && (
                      <FeedbackAnswer
                        answer={managerAnsMapped}
                        employeeName={managerName}
                        side="left"
                      />
                    )}
                  </FeedbackAnswerSection>
                </FeedbackCard>
              )}
            </Col>
          )}
          <Col>
            <FeedbackCard>
              <EmployeeFeedbackField
                answer={answer}
                onUpdateAnswer={onUpdateAnswer}
                readOnly={readOnly}
              />
            </FeedbackCard>
          </Col>
        </Row>
      );
    })}
  </EmployeeFeedbackAccordion>
);

export const EmployeeFeedbackQuestionnaire: React.FC<
  React.PropsWithChildren<Props>
> = ({
  answers,
  setIsComplete,
  onAnswerToSave,
  seeAllQuestions,
  readOnly = false,
  isPeerReviewer,
  employeeAnswers,
  managerAnswers,
  questionsSettings,
}) => {
  const [updatedAnswers, setUpdatedAnswers] = useState<
    IEmployeeFeedbackAnswer[]
  >(() =>
    answers.map((answer) => {
      const isVisible = isPeerReviewer
        ? visibleForPeers(answer)
        : visibleForManager(answer);

      const showOnlyQuestionName = seeAllQuestions && !isVisible;
      return {
        id: answer.Id,
        answer: answer.flair__Feedback__c,
        question: answer.question.flair__Question__c,
        description: answer.question.flair__Description__c,
        hint: answer.question.flair__Hint__c,
        choices: answer.question.flair__Choices__c,
        requiredChoices: answer.question.flair__Required_Choice__c,
        notes: answer.flair__Note__c,
        section: answer.question.flair__Section_Name__c,
        type: answer.question.flair__Feedback_Question_Type__c,
        showOptionalComment: answer.question.flair__Optional_Comment__c,
        questionOriginal: answer.question,
        isOptional:
          showOnlyQuestionName ||
          isFieldOptional(
            answer.question.flair__Optional__c,
            answer.question.flair__Feedback_Question_Type__c,
            answer.question.flair__Required_Choice__c,
          ),
        isComplete:
          showOnlyQuestionName ||
          isFieldComplete(
            answer.flair__Feedback__c,
            answer.question.flair__Optional__c,
            answer.question.flair__Feedback_Question_Type__c,
            answer.question.flair__Required_Choice__c,
          ),
        showOnlyQuestionName,
      };
    }),
  );

  const onUpdateAnswer = useCallback(
    (answer: IEmployeeFeedbackAnswer, value: string, notes: string) => {
      const answerToSend: UpdateEmployeeFeedbackAnswerInput = {
        id: answer.id,
        answer: value,
        notes: notes,
      };
      onAnswerToSave(answerToSend);
      setUpdatedAnswers((answers) => {
        const index = answers.findIndex((a) => a.id === answer.id);

        if (index === -1) {
          return answers;
        }

        const updatedAnswers = [...answers];

        updatedAnswers[index] = {
          ...answer,
          answer: value,
          isComplete: isFieldComplete(
            value,
            answer.isOptional,
            answer.type,
            answer.requiredChoices,
          ),
        };

        return updatedAnswers;
      });
    },
    [setUpdatedAnswers, onAnswerToSave],
  );

  useEffect(() => {
    setIsComplete(updatedAnswers.every((a) => a.isComplete));
  }, [updatedAnswers, setIsComplete]);

  // Group answers by section
  const sections = updatedAnswers.reduce(
    (result, answer) => {
      const sectionName = answer.section;
      const sectionIndex = result.findIndex(
        (s) => s.sectionName === sectionName,
      );

      if (sectionIndex !== -1) {
        const section = result[sectionIndex];

        // append the new answer to that section
        result[sectionIndex] = {
          ...section,
          answers: [...section.answers, answer],
        };

        return result;
      }

      // append a new section with the new answer
      return [
        ...result,
        {
          sectionName: sectionName,
          answers: [answer],
        },
      ];
    },
    [] as {
      sectionName: string | null;
      answers: IEmployeeFeedbackAnswer[];
    }[],
  );

  return (
    <FeedbackCycleSettingsProvider
      ratingMin={questionsSettings.ratingMin}
      ratingMax={questionsSettings.ratingMax}>
      <div>
        {sections.map((section) => (
          <Section
            key={section.sectionName ?? 'unknow-section'}
            sectionName={section.sectionName}
            answers={section.answers}
            onUpdateAnswer={onUpdateAnswer}
            readOnly={readOnly}
            employeeAnswers={employeeAnswers}
            managerAnswers={managerAnswers}
          />
        ))}
      </div>
    </FeedbackCycleSettingsProvider>
  );
};
